import React, { Component } from 'react';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Grid, InputAdornment, Pagination, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography } from '@mui/material';
import { CartesianGrid, ComposedChart, Bar, Line, Legend, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import moment from 'moment';
import SearchIcon from '@mui/icons-material/Search';
import WebIcon from '@mui/icons-material/Web';
import InsightsIcon from '@mui/icons-material/Insights';
import CloseIcon from '@mui/icons-material/Close';
import CustomizedSnackbars from '../../components/material-snackbars';
import Api from '../../services/api';
import UsageService from '../../services/usage.service';
import UsagePeriodFilter, { presetRange } from '../../components/usage/usage-period-filter';
import UsageVisitAccordion from '../../components/usage/usage-visit-accordion';
import UsageEventDrawer from '../../components/usage/usage-event-drawer';
import { UsageEllipsisCell, UsageKpiCard, UsageEmpty, UsageBlockTitle, UsageDurationBar, SELECTED_ROW_CLASS, fmtDateTime, fmtMs, fmtInt, durationColor } from '../../components/usage/usage-widgets';
import { t } from '../../components/usage/usage-i18n';

// ---------------------------------------------------------------------------
// Aba "Telas": TODAS as telas do período (rollup, paginado, busca) numa tabela ordenável.
// Clicar numa tela abre o painel dela: tendência por dia (rollup, byDay com screenRoute) e as
// VISITAS (fato, índice S4, ≤ 90 dias) com quem abriu, quando, IP e os requests filhos.
// Chega com ?start=&end= (dashboard → "ver todas") ou ?screenRoute= (link direto).
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;
const VISITS_PAGE = 20;

function parseQuery(search) {
  const q = new URLSearchParams(search || '');
  const start = q.get('start'); const end = q.get('end');
  const range = start && end
    ? { preset: 'custom', dateStart: moment(start).startOf('day').format('YYYY/MM/DD HH:mm:ss'), dateEnd: moment(end).endOf('day').format('YYYY/MM/DD HH:mm:ss') }
    : { preset: '7d', ...presetRange('7d') };
  return { filter: { ...range, api: '', actionGroup: '', userId: 0, userName: '' }, screenRoute: q.get('screenRoute') || '' };
}

export default class UsageScreens extends Component {
  constructor(props) {
    super(props);
    const q = parseQuery(props.location?.search);
    this.state = {
      filter: q.filter, search: '',
      loading: false, loaded: false, rows: [], count: 0, page: 1,
      sort: { key: 'visits', dir: 'desc' },
      selected: q.screenRoute, byDay: [], visits: [], visitsCount: 0, visitsPage: 1, visitsLoading: false, visitsTooLong: false,
      drawerRow: null, snack: { open: false, variant: 'error', message: '' },
    };
    this.onFilter = this.onFilter.bind(this);
    this.load = this.load.bind(this);
    this.onPage = this.onPage.bind(this);
    this.select = this.select.bind(this);
    this.loadVisits = this.loadVisits.bind(this);
    this.loadVisitRows = this.loadVisitRows.bind(this);
    this.openRow = this.openRow.bind(this);
    this.goUser = this.goUser.bind(this);
    this.closeSnack = this.closeSnack.bind(this);
  }

  componentDidMount() {
    this.lastSearch = this.props.location?.search || '';
    this.load(1);
    if (this.state.selected) this.select(this.state.selected, true);
  }

  // Montada permanentemente. Querystring nova com a aba ativa → novos filtros (+ tela, se vier).
  componentDidUpdate() {
    const search = this.props.location?.search || '';
    if (this.props.active && search && search !== this.lastSearch) {
      this.lastSearch = search;
      const q = parseQuery(search);
      this.setState({ filter: q.filter, selected: q.screenRoute, visits: [], byDay: [] }, () => { this.load(1); if (q.screenRoute) this.select(q.screenRoute, true); });
    }
  }

  tr(id, fallback) { return t(this.props.intl, id, fallback); }
  onFilter(next) { this.setState({ filter: next }); }
  onPage(e, page) { this.load(page); }
  openRow(row) { this.setState({ drawerRow: row }); }
  closeSnack() { this.setState({ snack: { ...this.state.snack, open: false } }); }
  notify(variant, message) { this.setState({ snack: { open: true, variant, message } }); }
  goUser(v) {
    const f = this.state.filter;
    if (!v.userId) return;
    this.props.history.push(`/${this.props.lang}/usage/user-activity?userId=${v.userId}&userName=${encodeURIComponent(v.userName || '')}&start=${moment(f.dateStart).format('YYYY-MM-DD')}&end=${moment(f.dateEnd).format('YYYY-MM-DD')}`);
  }

  load(page) {
    const f = this.state.filter;
    this.setState({ loading: true, page });
    UsageService.screens(f, this.state.search, page, PAGE_SIZE)
      .then((r) => this.setState({ loading: false, loaded: true, rows: r.data, count: r.count }))
      .catch((err) => { Api.kickoff(err); this.setState({ loading: false }); this.notify('error', err.message); });
  }

  // Seleciona uma tela: tendência (rollup) + visitas (fato). Reabrir a mesma tela fecha o painel.
  select(screenRoute, force) {
    if (!force && this.state.selected === screenRoute) { this.setState({ selected: '', byDay: [], visits: [], visitsCount: 0 }); return; }
    const f = this.state.filter;
    const days = moment(f.dateEnd).diff(moment(f.dateStart), 'days') + 1;
    this.setState({ selected: screenRoute, byDay: [], visits: [], visitsCount: 0, visitsPage: 1, visitsTooLong: days > 90, visitsLoading: true });
    const trend = UsageService.byDay({ ...f, screenRoute });
    const visits = days > 90 ? Promise.resolve({ data: [], count: 0 }) : UsageService.screenVisits(screenRoute, f.dateStart, f.dateEnd, 1, VISITS_PAGE);
    Promise.all([trend, visits])
      .then(([byDay, v]) => this.setState({ byDay, visits: v.data, visitsCount: v.count, visitsLoading: false }))
      .catch((err) => { Api.kickoff(err); this.setState({ visitsLoading: false }); this.notify('error', err.message); });
  }

  loadVisits(e, page) {
    const f = this.state.filter;
    this.setState({ visitsLoading: true, visitsPage: page });
    UsageService.screenVisits(this.state.selected, f.dateStart, f.dateEnd, page, VISITS_PAGE)
      .then((v) => this.setState({ visits: v.data, visitsCount: v.count, visitsLoading: false }))
      .catch((err) => { Api.kickoff(err); this.setState({ visitsLoading: false }); this.notify('error', err.message); });
  }

  loadVisitRows(id) { return UsageService.visitRows(id); }

  sortBy(key) { this.setState((s) => ({ sort: { key, dir: s.sort.key === key && s.sort.dir === 'desc' ? 'asc' : 'desc' } })); }

  renderTable() {
    const { intl } = this.props;
    const { rows, sort, selected } = this.state;
    if (!rows.length) return <UsageEmpty intl={intl} compact title={this.tr('usage.screens.none', 'Nenhuma tela no período')} />;
    const cols = [
      ['screenRoute', this.tr('usage.col.screen', 'Tela'), 'left'], ['visits', this.tr('usage.screens.col.visits', 'Visitas'), 'right'], ['users', this.tr('usage.screens.col.users', 'Usuários'), 'right'],
      ['requests', this.tr('usage.screens.col.requests', 'Requests'), 'right'], ['requestsPerVisit', this.tr('usage.screens.col.perVisit', 'Req/visita'), 'right'],
      ['avgDurationMs', this.tr('usage.screens.col.avg', 'Média API'), 'left'], ['maxDurationMs', this.tr('usage.screens.col.max', 'Máx'), 'right'],
      ['errors', this.tr('usage.col.errors', 'Erros'), 'right'], ['denials', this.tr('usage.col.denials', 'Recusas'), 'right'],
      ['writes', this.tr('usage.col.writes', 'Escritas'), 'right'], ['exports', this.tr('usage.col.exports', 'Exportações'), 'right'], ['lastDay', this.tr('usage.screens.col.lastDay', 'Último uso'), 'left'],
    ];
    const sorted = [...rows].sort((a, b) => {
      const va = a[sort.key]; const vb = b[sort.key];
      const r = typeof va === 'string' ? String(va).localeCompare(String(vb)) : (Number(va) || 0) - (Number(vb) || 0);
      return sort.dir === 'asc' ? r : -r;
    });
    const maxMs = Math.max(1000, ...rows.map((r) => r.avgDurationMs));
    return (
      <Box style={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow>{cols.map(([k, l, al]) => <TableCell key={k} align={al}><TableSortLabel active={sort.key === k} direction={sort.key === k ? sort.dir : 'desc'} onClick={() => this.sortBy(k)}>{l}</TableSortLabel></TableCell>)}</TableRow></TableHead>
          <TableBody>
            {sorted.map((r) => (
              <TableRow key={r.screenRoute} hover selected={selected === r.screenRoute} className={selected === r.screenRoute ? SELECTED_ROW_CLASS : undefined} style={{ cursor: 'pointer' }} onClick={() => this.select(r.screenRoute)}>
                <TableCell style={{ fontWeight: selected === r.screenRoute ? 700 : 500 }}><UsageEllipsisCell text={r.screenRoute} maxWidth={320} /></TableCell>
                <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}><b>{fmtInt(r.visits)}</b></TableCell>
                <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(r.users)}</TableCell>
                <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(r.requests)}</TableCell>
                <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{r.requestsPerVisit}</TableCell>
                <TableCell><UsageDurationBar ms={r.avgDurationMs} max={maxMs} /></TableCell>
                <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums', color: durationColor(r.maxDurationMs) }}>{fmtMs(r.maxDurationMs)}</TableCell>
                <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums', color: r.errors ? '#c62828' : undefined }}>{fmtInt(r.errors)}</TableCell>
                <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums', color: r.denials ? '#c62828' : undefined }}>{fmtInt(r.denials)}</TableCell>
                <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(r.writes)}</TableCell>
                <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(r.exports)}</TableCell>
                <TableCell style={{ whiteSpace: 'nowrap', color: '#666' }}>{r.lastDay ? moment(r.lastDay).format('DD/MM/YYYY') : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  }

  renderSelected() {
    const { intl } = this.props;
    const { selected, byDay, visits, visitsCount, visitsPage, visitsLoading, visitsTooLong, rows, filter } = this.state;
    if (!selected) return null;
    const row = rows.find((r) => r.screenRoute === selected);
    const trend = byDay.map((d) => ({ ...d, label: moment(d.day).format('DD/MM') }));
    const goDash = () => this.props.history.push(`/${this.props.lang}/usage/dashboard?preset=custom&start=${moment(filter.dateStart).format('YYYY-MM-DD')}&end=${moment(filter.dateEnd).format('YYYY-MM-DD')}&screen=${encodeURIComponent(selected)}`);
    return (
      <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12, borderLeft: '4px solid #9C27B0', marginTop: 12 }}>
        <CardContent>
          <UsageBlockTitle title={selected} subtitle={this.tr('usage.screens.selected.subtitle', 'Tendência no período e cada visita, com quem abriu e o que fez')} right={
            <Box display="flex" style={{ gap: 4 }}>
              <Button size="small" startIcon={<InsightsIcon />} onClick={goDash}>{this.tr('usage.screens.openDash', 'ver no dashboard')}</Button>
              <Button size="small" startIcon={<CloseIcon />} onClick={() => this.select(selected)}>{this.tr('usage.screens.close', 'fechar')}</Button>
            </Box>} />
          {row && (
            <Grid container spacing={2} style={{ marginBottom: 8 }}>
              <Grid item xs={6} sm={3} lg={1.5}><UsageKpiCard intl={intl} title={this.tr('usage.screens.col.visits', 'Visitas')} value={row.visits} color="#9C27B0" /></Grid>
              <Grid item xs={6} sm={3} lg={1.5}><UsageKpiCard intl={intl} title={this.tr('usage.screens.col.users', 'Usuários')} value={row.users} color="#009688" /></Grid>
              <Grid item xs={6} sm={3} lg={1.5}><UsageKpiCard intl={intl} title={this.tr('usage.screens.col.requests', 'Requests')} value={row.requests} color="#3F51B4" subtitle={`${row.requestsPerVisit} ${this.tr('usage.screens.perVisitShort', 'por visita')}`} /></Grid>
              <Grid item xs={6} sm={3} lg={1.5}><UsageKpiCard intl={intl} title={this.tr('usage.screens.col.avg', 'Média API')} value={row.avgDurationMs} format={fmtMs} color="#607D8B" subtitle={`${this.tr('usage.dash.maxLine', 'máximo')} ${fmtMs(row.maxDurationMs)}`} /></Grid>
              <Grid item xs={6} sm={3} lg={1.5}><UsageKpiCard intl={intl} title={this.tr('usage.col.errors', 'Erros')} value={row.errors} color="#B71C1C" /></Grid>
              <Grid item xs={6} sm={3} lg={1.5}><UsageKpiCard intl={intl} title={this.tr('usage.col.denials', 'Recusas')} value={row.denials} color="#F44336" /></Grid>
              <Grid item xs={6} sm={3} lg={1.5}><UsageKpiCard intl={intl} title={this.tr('usage.col.writes', 'Escritas')} value={row.writes} color="#8BC34A" /></Grid>
              <Grid item xs={6} sm={3} lg={1.5}><UsageKpiCard intl={intl} title={this.tr('usage.col.exports', 'Exportações')} value={row.exports} color="#FF9800" /></Grid>
            </Grid>
          )}
          {trend.length > 1 && (
            <ResponsiveContainer width="100%" height={200}>
              <ComposedChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="label" /><YAxis yAxisId="l" allowDecimals={false} /><YAxis yAxisId="r" orientation="right" allowDecimals={false} />
                <ChartTooltip formatter={(v) => fmtInt(v)} /><Legend />
                <Bar yAxisId="l" dataKey="screens" name={this.tr('usage.screens.col.visits', 'Visitas')} fill="#9C27B0" radius={[3, 3, 0, 0]} />
                <Line yAxisId="r" type="monotone" dataKey="users" name={this.tr('usage.screens.col.users', 'Usuários')} stroke="#009688" strokeWidth={2} dot={false} />
                <Line yAxisId="l" type="monotone" dataKey="denials" name={this.tr('usage.col.denials', 'Recusas')} stroke="#F44336" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
          <Box style={{ marginTop: 12 }}>
            <UsageBlockTitle title={this.tr('usage.screens.visits.title', 'Visitas')} subtitle={visitsTooLong ? this.tr('usage.screens.visits.tooLong', 'Período acima de 90 dias: reduza o período para listar as visitas (leitura do histórico detalhado).') : `${fmtInt(visitsCount)} ${this.tr('usage.screens.visits.inPeriod', 'no período · clique no usuário para abrir a linha do tempo')}`} />
            {visitsLoading && <Box display="flex" justifyContent="center" style={{ padding: 12 }}><CircularProgress size={22} color="secondary" /></Box>}
            {!visitsLoading && !visitsTooLong && visits.length === 0 && <UsageEmpty intl={intl} compact title={this.tr('usage.screens.visits.none', 'Nenhuma visita registrada no período')} />}
            {!visitsLoading && visits.map((v) => <UsageVisitAccordion key={v.screenVisitId} intl={intl} visit={v} loadRows={this.loadVisitRows} onRowClick={this.openRow} onUserClick={this.goUser} />)}
            {!visitsLoading && visitsCount > VISITS_PAGE && <Box style={{ marginTop: 8 }}><Pagination count={Math.ceil(visitsCount / VISITS_PAGE)} page={visitsPage} onChange={this.loadVisits} /></Box>}
          </Box>
        </CardContent>
      </Card>
    );
  }

  render() {
    const { intl } = this.props;
    const { filter, search, loading, loaded, count, page, drawerRow, snack } = this.state;
    const extra = (
      <Grid item xs={12} sm={6} md={3}>
        <TextField fullWidth size="small" label={this.tr('usage.screens.search', 'Buscar tela')} value={search} InputLabelProps={{ shrink: true }} placeholder="/called-accompaniment"
          onChange={(e) => this.setState({ search: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && this.load(1)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      </Grid>
    );
    return (
      <Box>
        <UsagePeriodFilter showIp={false} intl={intl} value={filter} onChange={this.onFilter} showGroup={false} extra={extra} onApply={() => this.load(1)} applying={loading} />
        <Box style={{ marginTop: 16 }}>
          {loading && <Box display="flex" justifyContent="center" style={{ padding: 16 }}><CircularProgress color="secondary" /></Box>}
          {!loading && !loaded && <UsageEmpty intl={intl} icon={<WebIcon style={{ fontSize: 56 }} />} />}
          {!loading && loaded && (
            <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12 }}>
              <CardContent style={{ paddingBottom: 8 }}>
                <UsageBlockTitle title={this.tr('usage.screens.title', 'Telas mais acessadas')} subtitle={`${fmtInt(count)} ${this.tr('usage.screens.subtitle', 'tela(s) no período · clique para ver tendência e visitas')}`}
                  right={filter.userId ? <Chip size="small" color="primary" label={`${this.tr('usage.ua.userChip', 'Usuário')}: ${filter.userName || `#${filter.userId}`}`} /> : null} />
                {this.renderTable()}
                {count > PAGE_SIZE && (
                  <Box display="flex" alignItems="center" style={{ marginTop: 8, gap: 12 }}>
                    <Pagination count={Math.ceil(count / PAGE_SIZE)} page={page} onChange={this.onPage} />
                    <Typography variant="caption" style={{ color: '#888' }}>{this.tr('usage.screens.sortHint', 'A ordenação por coluna vale para a página atual')}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
          {!loading && this.renderSelected()}
        </Box>
        <UsageEventDrawer intl={intl} open={!!drawerRow} row={drawerRow} onClose={() => this.setState({ drawerRow: null })} lang={this.props.lang} history={this.props.history} onCopy={() => this.notify('success', this.tr('usage.copied', 'Copiado!'))} />
        <CustomizedSnackbars variant={snack.variant} message={snack.message} isOpen={snack.open} toClose={this.closeSnack} />
      </Box>
    );
  }
}
